import { LoadingService } from 'src/app/services/loading.service';
import { Component, OnInit, Renderer2, Input, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration } from 'chart.js';
import { Test } from 'src/app/models/test.model';
import { ReportService } from 'src/app/services/report.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AppConfig } from 'src/app/helpers/app.config';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import * as html2pdf from 'html2pdf.js';
import * as seedrandom from "seedrandom";

@Component({
  selector: 'ct-adn-report',
  templateUrl: './adn-report.component.html',
  styleUrls: ['./adn-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdnReportComponent implements OnInit {

  @Input() test: Test;

  separator = '<!-- Separator -->';

  public language: string;
  private languageId: number;
  public currentDate = new Date();
  public data: number[];
  public reportType: 'candidate' | 'consultant' | 'client' | 'executive';
  public xLabels;
  public testModel;

  public chartScoreLabels: string[] = [];
  public chartScoreOptions: ChartConfiguration<'pie'>['options'];
  public chartScorePlugins = [DataLabelsPlugin];
  public chartScoreData: ChartConfiguration<'pie'>['data'];

  public chartAdjustmentLabels: string[] = [];
  public chartAdjustmentOptions: ChartConfiguration<'radar'>['options'];
  public chartAdjustmentPlugins = [];
  public chartAdjustmentData: ChartConfiguration<'radar'>['data'];

  public chartScoreBase10Labels: string[] = [];
  public chartScoreBase10Options: ChartConfiguration<'radar'>['options'];
  public chartScoreBase10Plugins = [DataLabelsPlugin];
  public chartScoreBase10Data: ChartConfiguration<'radar'>['data'];

  public scoresParse;
  public scoresParseInterviewFiltered;
  public scoresParseStrenghtsFiltered;
  public scoresParseTipsFiltered;
  public textVariationParagraph1;
  public textVariationParagraph2;
  public textTipsVariations = {};
  public timeSpentRounded;

  public idealProfileName: string;

  public AppConfig = AppConfig

  private computedBodyStyle = getComputedStyle(document.body)

  public colors = {
    primaryColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
    secondaryColor: this.computedBodyStyle.getPropertyValue('--color-secondary'),
    tertiaryColor: this.computedBodyStyle.getPropertyValue('--color-tertiary'),
    quaternaryColor: this.computedBodyStyle.getPropertyValue('--color-quaternary'),
    quinaryColor: this.computedBodyStyle.getPropertyValue('--color-quinary'),
    senaryColor: this.computedBodyStyle.getPropertyValue('--color-senary'),
    septenaryColor: this.computedBodyStyle.getPropertyValue('--color-septenary'),
    octonaryColor: this.computedBodyStyle.getPropertyValue('--color-octonary'),
    nonaryColor: this.computedBodyStyle.getPropertyValue('--color-nonary')
  }

  // corregido para ESFBITE-1342 por pentest para evitar XSS
  public get sanitizedCandidateName(): string {
    return (this.test?.candidate?.name || '').replace(/[<>"'&]/g, '');
  }

  constructor(
    public renderer: Renderer2,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    public reportService: ReportService,
    public utilsService: UtilsService,
    public loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.snapshot.queryParamMap.get('languageId')
      ? this.languageId = Number(this.activatedRoute.snapshot.queryParamMap.get('languageId'))
      : this.languageId = Number(this.test.reportLanguageId);
    this.language = this.utilsService.getLanguageFromLanguageId(this.languageId);
    this.translateService.setDefaultLang(this.language)

    // En el caso de tenerlo seteamos los valores del proyecto
    if (this.test.projectSelected && this.test.projectSelected.projectId) {

      this.colors = {
        primaryColor: this.test.projectSelected.projectPrimaryColor,
        secondaryColor: this.test.projectSelected.projectSecondaryColor,
        tertiaryColor: this.test.projectSelected.projectTertiaryColor,
        quaternaryColor: this.test.projectSelected.projectQuaternaryColor,
        quinaryColor: this.test.projectSelected.projectQuinaryColor,
        senaryColor: this.test.projectSelected.projectSenaryColor,
        septenaryColor: this.test.projectSelected.projectSeptenaryColor,
        octonaryColor: this.test.projectSelected.projectOctonaryColor,
        nonaryColor: this.test.projectSelected.projectNonaryColor
      }
      document.getElementById('reportModule').style.setProperty('--color-primary', this.colors.primaryColor);
      document.getElementById('reportModule').style.setProperty('--color-secondary', this.colors.secondaryColor);
      document.getElementById('reportModule').style.setProperty('--color-tertiary', this.colors.tertiaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quaternary', this.colors.quaternaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quinary', this.colors.quinaryColor);
      document.getElementById('reportModule').style.setProperty('--color-senary', this.colors.senaryColor);
      document.getElementById('reportModule').style.setProperty('--color-septenary', this.colors.septenaryColor);
      document.getElementById('reportModule').style.setProperty('--color-octonary', this.colors.octonaryColor);
      document.getElementById('reportModule').style.setProperty('--color-nonary', this.colors.nonaryColor);

      this.translateService.use('projects__' + this.test.projectSelected.projectId + '__' + this.language);
    } else {
      this.translateService.use(this.language);
      document.documentElement.lang = this.language.split('-')[0];

    }

    this.testModel = this.test.testModelId === 1 ? 'NR' : 'IP';

    this.textVariationParagraph1 = this.getTextVariation(1);
    this.textVariationParagraph2 = this.getTextVariation(2);
    switch (this.test.reportTypeId) {
      case '1': this.reportType = 'candidate'; break;
      case '2': this.reportType = 'consultant'; break;
      case '3': this.reportType = 'client'; break;
      case '4': this.reportType = 'executive'; break;
    }

    if (this.test.result.seconds) {
      this.timeSpentRounded = Math.ceil(Number(this.test.result.seconds) / 60)
    } else {
      // Convertimos el tiempo empleado a minutos redondeando los s
      let addHours = 0;
      if (this.test.result.timeSpent.includes('h')) {
        addHours = Number(this.test.result.timeSpent.split('h ')[0])
        this.test.result.timeSpent = this.test.result.timeSpent.split('h ')[1]
      }
      if (this.test.result.timeSpent.split('m ')[1]) {
        this.timeSpentRounded = Number(this.test.result.timeSpent.split('m ')[1].slice(0, -1)) >= 30
          ? Number(this.test.result.timeSpent.split('m ')[0]) + 1 + addHours * 60
          : Number(this.test.result.timeSpent.split('m ')[0]) + addHours * 60
      }
    }

    this.getTipsVariations();

    this.getIdealProfileTranslation(this.test.idealProfileName)

    setTimeout(() => {
      this.adnChart();
    }, 500);
  }

  bowserprint() {
    const fileName = this.utilsService.reportFileName(this.test, this.language, this.reportType);
    const token = this.activatedRoute.snapshot.paramMap.get('token');

    this.reportService.getAdnPdfReport({ token, languageId: this.languageId }, fileName).subscribe(pdf => {
      const downloadLink = document.createElement('a');
      downloadLink.href = pdf;
      downloadLink.download = '';
      downloadLink.click();
    }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + 'report/pdf', body: { token: token, reportLanguageId: this.languageId }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } })));
  }

  print() {
    this.loadingService.startLoading();
    window.scrollTo(0, 0);
    this.renderer.setStyle(document.querySelector('body'), 'overflow', 'hidden');
    window.addEventListener('scroll', this.scrollToTop);
    document.querySelectorAll('#adnReport').forEach(element => this.renderer.setAttribute(element, 'class', 'print'));

    document.querySelector('#adnChartImg').setAttribute('hidden', 'true');
    document.querySelector('#adnChart').removeAttribute('hidden');

    // reducción del tamaño del gráfico para la versión pdf
    const chartScore = document.querySelector<HTMLElement>('#chartScore')
    chartScore.style.width = '600px'

    const chartAdjustment = document.querySelector<HTMLElement>('#chartAdjustment')
    chartAdjustment.style.width = '600px'
    chartAdjustment.style.marginTop = '-600px'

    const chartScoreBase10 = document.querySelector<HTMLElement>('#chartScoreBase10')
    if (chartScoreBase10) {
      chartScoreBase10.style.width = '600px'
      chartScoreBase10.style.marginTop = '-600px'
    }

    const fileName = this.utilsService.reportFileName(this.test, this.language, this.reportType);

    return new Promise<void>(resolve => setTimeout(() => {
      let worker = html2pdf().from(document.querySelectorAll('#adnReport')[0]).set({
        pagebreak: { before: '.page, .break', avoid: 'p' },
        // Reducir la escala en los informes mas largos
        html2canvas: { scale: 1.8 },
        image: { type: 'jpeg', quality: 0.8 },
        filename: fileName
      }).toPdf();

      const pages = [...Array.from(document.querySelectorAll('#adnReport'))];
      for (const [i, page] of pages.slice(1).entries()) {
        worker = worker.get('pdf').then(pdf => {
          pdf.addPage();
        }).from(page).toContainer().toCanvas().toPdf();
      }

      worker = worker.get('pdf').then(pdf => this.addHeaderAndFooter(pdf)).save().then(() => {
        this.renderer.setStyle(document.querySelector('body'), 'overflow', 'initial');
        window.removeEventListener('scroll', this.scrollToTop);
        document.querySelectorAll('#adnReport').forEach(element => this.renderer.removeAttribute(element, 'class'));
        resolve();
      }).get('pdf').then(pdf => {
        // Escribir el base64 del pdf en el html para obtenerlo desde bowserprint
        document.querySelector('#base64').textContent = pdf.output('datauristring').toString().split('base64,')[1];
        document.querySelector('#base64').classList.add('base64');
      });

      setTimeout(() => {
        this.loadingService.stopLoading();
      }, 1000);

    }, 500));
  }

  private scrollToTop() {
    window.scrollTo(0, 0);
  }

  private addHeaderAndFooter(pdf) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 2; i <= totalPages - 1; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(this.computedBodyStyle.getPropertyValue('--color-tertiary').trim());

      const relevateImg = new Image();
      relevateImg.src = 'assets/images/relevate-talent-assessment-logo.png';
      pdf.addImage(relevateImg, 'png', 15, 10, 55, 18);

      const randstadImg = new Image();
      randstadImg.src = 'assets/images/randstad-icon-pdf.png';
      pdf.addImage(randstadImg, 'png', 15, pdf.internal.pageSize.getHeight() - 19, 8, 4);

      pdf.text(
        (this.test.candidate.name
          ? this.utilsService.initials(this.test.candidate)
          : this.test.candidate.randstadCandidate) + ' | ' + (this.test.invitationId != 0 ? this.test.invitationId : this.test.externalId) + ' | ' + (this.test.testModelId === 1 ? 'NR' : 'IP') + '/02/' + (this.reportType === 'candidate' ? '' : (this.test.adjustment ? 'CA/' : 'SA/')) + 'SP', 25, pdf.internal.pageSize.getHeight() - 15);

      pdf.text((i).toString(), pdf.internal.pageSize.getWidth() - 15, pdf.internal.pageSize.getHeight() - 15);
    }
  }


  adnChart() {
    this.scoresParse = this.test.adnScore;
    const scoresParseChartSorted = [...this.test.adnScore].sort((a, b) => a.questionGroupId > b.questionGroupId ? 1 : -1);
    scoresParseChartSorted.map(score => !score.idealProfileMinValue ? score.idealProfileMinValue = 0 : null);

    this.scoresParseInterviewFiltered = this.scoresParse.filter(skill => skill.rangeId === 1 || skill.rangeId === 4);
    this.scoresParseTipsFiltered = this.scoresParse.filter(skill => skill.rangeId === 1 || skill.rangeId === 2);
    this.scoresParseStrenghtsFiltered = this.scoresParse.filter(skill => skill.rangeId === 3 || skill.rangeId === 4);

    const labels = [];
    const tooltips = [];
    const scores = [];
    const scoresBase10 = [];
    const adjustment = [];
    const values = [];

    let management = this.computedBodyStyle.getPropertyValue('--color-tertiary');
    let social = this.computedBodyStyle.getPropertyValue('--color-senary');
    let individual = this.computedBodyStyle.getPropertyValue('--color-secondary');
    let task = this.computedBodyStyle.getPropertyValue('--color-octonary');
    let compentencesAreaColor = [];
    const onlyProfileSkills = false;

    for (const score of scoresParseChartSorted) {
      // se toman en cuenta todas las puntuaciones o solo las puntuaciones que contempla el perfil ideal
      if (!onlyProfileSkills || (onlyProfileSkills && score.idealProfileMinValue)) {
        scores.push(this.base10ToBase4(score.value));
        scoresBase10.push(score.value);
        adjustment.push(score.idealProfileMinValue);
        values.push(1);
        labels.push(this.translateService.instant('ADNREPORT.SKILLS.' + score.questionGroupAbbreviation.toUpperCase() + '.NAME'));

        const tooltipBaseKey = 'ADNREPORT.SKILLS.';

        switch (score.questionGroupId) {
          case 13:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(individual);
            break;
          case 14:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(individual);
            break;
          case 15:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(individual);
            break;
          case 16:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(individual);
            break;
          case 17:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(individual);
            break;
          case 18:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(task);
            break;
          case 19:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(task);
            break;
          case 20:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(task);
            break;
          case 21:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(social);
            break;
          case 22:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(social);
            break;
          case 23:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(social);
            break;
          case 24:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(social);
            break;
          case 25:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(social);
            break;
          case 26:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(management);
            break;
          case 27:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(management);
            break;
          case 28:
            tooltips.push(this.translateService.instant(tooltipBaseKey + score.questionGroupAbbreviation.toUpperCase() + '.DESCRIPTION'));
            compentencesAreaColor.push(management);
            break;
        }
      }
    }

    const levelsAchieved = {
      1: this.translateService.instant('REPORT.CT.ADN.GENERAL.RESULTSCHART.CHART.LEGEND.LOW_DEVELOPMENT'),
      2: this.translateService.instant('REPORT.CT.ADN.GENERAL.RESULTSCHART.CHART.LEGEND.MEDIUM_DEVELOPMENT'),
      3: this.translateService.instant('REPORT.CT.ADN.GENERAL.RESULTSCHART.CHART.LEGEND.GOOD_DEVELOPMENT'),
      4: this.translateService.instant('REPORT.CT.ADN.GENERAL.RESULTSCHART.CHART.LEGEND.EXCELENT_DEVELOPMENT')
    };

    this.chartScoreLabels = Array(4).fill(labels).flat();
    this.chartScoreData = {
      labels: Array(4).fill(labels).flat(),
      datasets: [
        {
          backgroundColor: this.scoreToColors(scores, 4, compentencesAreaColor),
          borderColor: '#eaeaea',
          borderWidth: 0.8,
          hoverBackgroundColor: this.scoreToColors(scores, 4, compentencesAreaColor, true),
          hoverBorderColor: this.scoreToColors(scores, 4, compentencesAreaColor, true),
          data: values
        },
        {
          backgroundColor: this.scoreToColors(scores, 3, compentencesAreaColor),
          borderColor: '#eaeaea',
          borderWidth: 0.8,
          hoverBackgroundColor: this.scoreToColors(scores, 3, compentencesAreaColor, true),
          hoverBorderColor: this.scoreToColors(scores, 3, compentencesAreaColor, true),
          data: values
        },
        {
          backgroundColor: this.scoreToColors(scores, 2, compentencesAreaColor),
          borderColor: '#eaeaea',
          borderWidth: 0.8,
          hoverBackgroundColor: this.scoreToColors(scores, 2, compentencesAreaColor, true),
          hoverBorderColor: this.scoreToColors(scores, 2, compentencesAreaColor, true),
          data: values
        },
        {
          backgroundColor: this.scoreToColors(scores, 1, compentencesAreaColor),
          borderColor: '#eaeaea',
          borderWidth: 0.8,
          hoverBackgroundColor: this.scoreToColors(scores, 1, compentencesAreaColor, true),
          hoverBorderColor: this.scoreToColors(scores, 1, compentencesAreaColor, true),
          data: values
        }
      ]
    };

    this.chartScoreOptions = {
      responsive: false,
      layout: {
        padding: 150
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              let label = context.chart.data.labels[context.dataIndex] as string;
              return [label.charAt(0).toUpperCase() + label.slice(1) + ': ' + levelsAchieved[scores[context.dataIndex]] + '.', '', ...this.splitIntoLines(tooltips[context.dataIndex], 60)];
            }
          }
        },
        datalabels: {
          align: 'end',
          anchor: 'end',
          offset: 20,
          color: this.computedBodyStyle.getPropertyValue('--color-primary'),
          formatter: (value, context) => {
            if (context.datasetIndex === 0) {
              return this.splitIntoLines(context.chart.data.labels[context.dataIndex], 16);
            } else {
              return '';
            }
          },
        },
      }
    };

    this.chartAdjustmentData = {
      labels: labels,
      datasets: [{
        data: adjustment,
        fill: false,
        borderColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
        pointBackgroundColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
        borderWidth: 1.5,
        tension: 0.4,
        datalabels: { display: false }
      }]
    };

    this.chartAdjustmentOptions = {
      responsive: false,
      layout: {
        padding: 150
      },
      plugins: {
        legend: { display: false }
      },
      scales: {
        r: {
          min: 0,
          max: 10,
          angleLines: { display: false },
          grid: { display: false },
          pointLabels: { display: false },
          ticks: {
            display: false,
            maxTicksLimit: 11,
          }
        }
      }
    };

    this.chartScoreBase10Data = {
      labels: labels,
      datasets: [{
        data: scoresBase10,
        fill: false,
        borderColor: '#00000000',
        pointBackgroundColor: '#00000000',
        pointBorderColor: '#00000000',
        tension: 0.4,
        datalabels: {
          rotation: -this.getChartRotation(values.length),
          color: '#0F1941',
          font: {
            size: 16,
            weight: 'bold'
          }
        }
      }]
    };

    this.chartScoreBase10Options = {
      responsive: false,
      layout: {
        padding: 150
      },
      plugins: {
        legend: { display: false }
      },
      scales: {
        r: {
          min: 0,
          max: 10,
          angleLines: { display: false },
          grid: { display: false },
          pointLabels: { display: false },
          ticks: {
            display: false,
            maxTicksLimit: 11,
          }
        }
      }
    };

    setTimeout(() => {
      const chartAdjustmentContainer = document.querySelector('#adnChart canvas:nth-child(2)') as HTMLElement;
      const chartScoreBase10Container = document.querySelector('#adnChart canvas:nth-child(3)') as HTMLElement;

      if (chartAdjustmentContainer) {
        chartAdjustmentContainer.style.transform = 'rotate(' + this.getChartRotation(values.length) + 'deg)';
      }

      if (chartScoreBase10Container) {
        chartScoreBase10Container.style.transform = 'rotate(' + this.getChartRotation(values.length) + 'deg)';
      }

      // Añadido para el informe ejecutivo de talent
      if (this.reportType === 'executive') {
        html2pdf().from(document.querySelector('#adnChart')).toCanvas().get('canvas').then(canvas => {
          document.querySelector('#adnChartImg').setAttribute('src', canvas.toDataURL());
          document.querySelector('#adnChart').setAttribute('hidden', 'true');
        });
      }
    }, 1000);

  }

  // Funciones auxiliares
  scoreToColors(scores, level, compentencesAreaColor, hover = false) {
    const compentencesAreaColorPerLevel = [...compentencesAreaColor]

    for (let i = 0; i < compentencesAreaColorPerLevel.length; i++) {
      if (scores[i] < level) {
        compentencesAreaColorPerLevel[i] = hover ? 'transparent' : 'white';
      }
    }
    return compentencesAreaColorPerLevel;
  }

  splitIntoLines(input, charLimit) {
    return input.split(/([\s-]+)/).reduce((output, item) => {
      let last = output.pop() || '';                // get the last item
      return last.length + item.length > charLimit  // would adding the current item to it exceed n chars?
        ? [...output, last, item.trim()]          // Yes: start a new item
        : [...output, last + item]                // No: add to previous item
    }, []);
  }

  base10ToBase4(base10Number) {
    if (base10Number <= 0) {
      return 0;
    } else if (base10Number <= 2) {
      return 1;
    } else if (base10Number <= 4) {
      return 2;
    } else if (base10Number <= 7) {
      return 3;
    } else if (base10Number <= 10) {
      return 4;
    }
  }

  getChartRotation(skills) {
    return 16 / skills * 11;
  }

  getTextVariation(paragraph) {
    switch (Date.parse(this.test.createdAt) % 10) {
      case 0:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_1' : 'PARAGRAPH_2.VARIATION_1'
      case 1:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_1' : 'PARAGRAPH_2.VARIATION_2'
      case 2:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_1' : 'PARAGRAPH_2.VARIATION_3'
      case 3:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_2' : 'PARAGRAPH_2.VARIATION_1'
      case 4:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_2' : 'PARAGRAPH_2.VARIATION_2'
      case 5:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_2' : 'PARAGRAPH_2.VARIATION_3'
      case 6:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_2' : 'PARAGRAPH_2.VARIATION_3'
      case 7:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_3' : 'PARAGRAPH_2.VARIATION_1'
      case 8:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_3' : 'PARAGRAPH_2.VARIATION_2'
      case 9:
        return paragraph === 1 ? 'PARAGRAPH_1.VARIATION_3' : 'PARAGRAPH_2.VARIATION_3'
    }
  }

  getTipsVariations() {
    // generamos una semilla para obener números aleatorios fijos basados en la fecha de creación del test
    const rng = seedrandom(Date.parse(this.test.createdAt));

    // Marcamos las posibles variaciones de textos de tips por skill
    let posibleVariationsBySkill = [13, 16, 16, 13, 18, 17, 17, 15, 18, 20, 18, 20, 15, 30, 12, 8];

    for (const [index, skillPosibleVariations] of posibleVariationsBySkill.entries()) {
      // generamos un array con todos los posibles valores de variaciones de la skill
      const x = Array.from(Array(skillPosibleVariations).keys())
      const min = 0
      let max = skillPosibleVariations - 1;
      const y = [];
      for (let i = 0; i < 4; i++) {
        // puseamos a un array los numeros aleatorios generados (lo que serán als variaciones elegidas)
        y.push(x.splice((Math.floor(rng() * (max - min + 1)) + min), 1)[0] + 1);
        // se reduce el max en cada iteración ya que hemos sacado el valor del array original
        max--;
      }
      // se rellena el objeto con clave (skillId): valor (array de variaciones seleccionadas)
      this.textTipsVariations[index + 1] = y;
    }
  }

  public enrichedSkill(skill, interview?): void {
    return { ...skill, reportType: this.test.reportTypeId, adjustment: this.test.adjustment, interview: interview }
  }

  getIdealProfileTranslation(idealProfileSpanish: string): void {
    let idealProfileKey: string = '';
    this.translateService.getTranslation('es-ES').subscribe(translations => {
      if (translations && translations['JOBTYPES']) {
        const idealProfiles = translations['JOBTYPES'];
        for (const key in idealProfiles) {
          if (idealProfiles[key] === idealProfileSpanish.toLowerCase()) {
            idealProfileKey = key;
          }
        }
      }
      this.idealProfileName = idealProfileKey ? this.translateService.instant('JOBTYPES.' + idealProfileKey) : idealProfileSpanish;
    });
  }

}
